<template>
  <appointment-schedule>
    <template v-slot:side-bar>
      <appointment-side-bar
        initial-category="employees"
        v-bind:appointment-near-list="appointmentNearList"
        v-bind:cabinet-list="cabinets"
        v-bind:employee-list="doctors"
        v-bind:initial-date="day"
        v-bind:initial-employee-id="employeeId"
        v-bind:initial-period="period"
        v-bind:initial-range="range"
        v-bind:is-collapsed="isCollapsedSideBar"
        v-bind:nearest-unconfirmed-appointment-count="nearestUnconfirmedAppointmentCount"
        v-bind:orders-by-aggregators="ordersByAggregators"
        v-bind:orders-by-widgets="ordersByWidgets"
        v-bind:prev-category="prevCategory"
        v-bind:selected-employee="selectedEmployee"
        v-bind:waiting-list-count="waitingList.length"
        v-on:collapse="onSidebarCollapsed"
        v-on:open-aggregator-orders="openAggregatorOrdersDialog"
        v-on:open-near-appointment="onOpenNearAppointment"
        v-on:open-nearest-unconfirmed-appointment-list="openNearestUnconfirmedAppointmentList"
        v-on:open-waiting-list="openWaitingList"
        v-on:open-free-doctor="toggleFreeDoctorControls"
        v-on:open-widget-orders="openWidgetOrdersDialog"
        v-on:open-work-time-controls="toggleDialogWorkTimeControls"
      />
      <dialog-add-employee-break
        v-bind:employeeName="selectedEmployee.name"
        v-bind:key="isShownEmployeeBreakDialog"
        v-bind:timeline="employeeBreakTimeline"
        v-model="isShownEmployeeBreakDialog"
        v-on:cancel="closeEmployeeBreakDialog"
        v-on:submit="onSubmitEmployeeBreak"
      />
    </template>
    <template v-slot:only-print>
      <h3>{{ viewDateTitle }}</h3>
      <h4 v-if="employeeId">{{ selectedEmployee.name }}</h4>
    </template>
    <appointment-schedule-head
      v-bind:date-title="dateTitle"
      v-bind:initial-date="day"
      v-on:collapse="onSidebarCollapsed(true)"
      v-on:open-day="onOpenDay"
    />
    <div
      class="schedule-appointment__body"
      ref="scheduleAppointmentTable">
      <time-line
        v-if="!loadedEmployeeListIsEmpty"
        v-bind:is-disable-export="isDisableExport"
        v-bind:rulerUnitCSS="$options.rulerUnitCSS"
        v-bind:timeline="timeline"
        v-bind:value="step"
        v-on:export-to-excel="exportToExcel"
        v-on:input="onInputStep"
      />
      <div class="schedule-appointment__timetable__grid">
        <article
          class="schedule-appointment__timetable__column"
          v-for="day in employee.days"
          v-bind:key="day.date"
          v-bind:ref="`scheduleAppointmentColumn-${day.date}`">
          <timetable-column-header
            v-bind:popover-disabled="employee.actions.length === 0"
            v-bind:subtitle="day.short_title"
            v-bind:title="day.weekday"
            v-bind:show-popover-print-dialog="true"
            v-on:print-column="printPage(day.date, $event)">
            <menu-employee-action
              v-bind:actions="employee.actions"
              v-bind:day="day.date"
              v-bind:employeeId="employee.id"
              v-on="createListenersEmployeeActions(employee.id)"
            />
          </timetable-column-header>
          <popover-menu
            absolute
            overflow-visible
            v-bind:position-x="popoverChangesWorkTimeX"
            v-bind:position-y="popoverChangesWorkTimeY"
            v-bind:value="day.date === selectedChangesWorkTimeDay"
          >
            <form-daily-time-range
              v-bind:initial-from="getPeriodStartBySlots(day.slots)"
              v-bind:initial-to="getPeriodEndBySlots(day.slots)"
              v-bind:suggestion-hours="day.suggested_hours"
              v-on:submit="changeEmployeeWorkHours"
              v-on:close-change-work-time-popover="clearSelectedChangesWorkTimeDay"
            />
          </popover-menu>
          <timetable-cell
            v-for="(slot, index) in day.slots"
            v-bind:can-move-appointment="canMoveAppointment(day)"
            v-bind:cell="slot"
            v-bind:drag-appointment="dragAppointment"
            v-bind:has-edit-breaks="hasEditBreaks"
            v-bind:expanded-slots="expandedSlots"
            v-bind:show-employee-name="false"
            v-bind:key="index"
            v-bind:has-view-patient-invoices="hasViewPatientInvoices"
            v-bind:date="day.date"
            v-bind:messengers-access="messengersAccess"
            v-on:click-disabled-cell="onClickDisabledCell"
            v-on:create-invoice="onCreateInvoice"
            v-on:open-invoices="onOpenInvoices"
            v-on:drag-appointment-end="onDragAppointmentEnd"
            v-on:drag-appointment-start="onDragAppointmentStart"
            v-on:initiate-call="onInitiateCallByAppointment"
            v-on:move-appointment="onMoveAppointment(
              $event,
              employee.id,
              day,
            )"
            v-on:open-appointment="onOpenDialog(
              $event.id,
              day.date,
              employee,
              $event.ends_time,
              $event.starts_time,
            )"
            v-on:open-break="openFormEmployeeBreakEdit(day.date, employee, slot)"
            v-on:open-context-menu="onOpenContextMenu(day.date, $event)"
            v-on:open-waitings="openWaitingList" />
        </article>
        <div
          v-if="loadedEmployeeListIsEmpty"
          class="schedule-appointment__notification">
          <button
            title="Открыть меню"
            class="schedule-appointment__collapse"
            type="button"
            v-on:click="onSidebarCollapsed(true)"
          />
          На {{ viewDate }} у врачей нет рабочих часов. Перейдите в раздел
          <router-link v-bind:to="{ name: 'ClinicPanelWorkSchedule' }">
            График работы</router-link>, чтобы добавить врачам рабочие дни.
        </div>
      </div>
    </div>
    <iframe
      class="schedule-appointment__print"
      ref="printView"
      title="printView"
    />
    <context-menu-appointment-slot
      v-if="!disableContextMenu"
      v-bind:attach="contextMenuTarget"
      v-bind:rejection-reason-list="rejectionReasons"
      v-bind:isCopyAppointmentAction="isCopyAppointmentAction"
      v-bind:isPasteAppointmentAction="isPasteAppointmentAction"
      v-bind:disabledPasteAppointmentAction="disabledPasteAppointmentAction"
      v-bind:isChangeStatusAvailable="isChangeStatusAvailable"
      v-bind:isGoToPatientMedicalCard="isGoToPatientMedicalCard"
      v-bind:isPrintFutureAppointments="isPrintFutureAppointments"
      v-bind:isShowCancelModal="isShowCancelModal"
      v-bind:input-errors="changeStatusInputErrors"
      v-bind:current-status="currentStatus"
      v-bind:position-x="offsetContextMenuX"
      v-bind:position-y="offsetContextMenuY"
      v-model="isShowSlotContextMenu"
      v-on:copy="onCopyAppointment"
      v-on:paste="onPasteAppointmentCopy"
      v-on:change-appointment-status="changeAppointmentStatus"
      v-on:delete-rejection-reason="onDeleteRejectionReason"
      v-on:transfer-to-medcard="onPatientMedicalCard"
      v-on:print-future-appointments="onPrintFutureAppointments"
      v-on:show-cancel-dialog="onShowCancelDialog"
    />
    <modal-dialog
      v-model="needConfirmAppointmentPaste"
      title="При вставке записи в этот временной слот, продолжительность визита будет обрезана. Вставить?"
      with-cross
    >
      <div class="modal-dialog__buttons">
        <button
          class="modal-dialog__cancel"
          type="button"
          v-on:click="closeConfirmPasteDialog">Нет</button>
        <button
          class="modal-dialog__agree"
          type="button"
          v-on:click="onPasteAppointment(appointmentSlotForPaste)">Да</button>
      </div>
    </modal-dialog>
    <template v-slot:side-dialogs>
      <confirm-side-dialog
        v-if="wasSelectedBreakSlot"
        label="Редактирование перерыва"
        v-bind:need-confirm="needConfirmBreakEditForm"
        v-on:cancel="selectedBreak = null">
        <form-employee-break-edit
          v-bind:allowed-dates="employeeBreakAllowedDates"
          v-bind:break-id="selectedBreak.id"
          v-bind:employee-list="appointmentWorkEmployees"
          v-bind:initial-employee-id="employee.id"
          v-bind:initial-date="selectedBreak.date"
          v-bind:initial-ends-time="selectedBreak.endsTime"
          v-bind:initial-note="selectedBreak.note"
          v-bind:initial-starts-time="selectedBreak.startsTime"
          v-bind:input-errors="breakInputErrors"
          v-bind:timeline="employeeBreakTimeline"
          v-bind:presets-duration="$options.presetsDuration"
          v-on:cancel-break="onCancelBreak"
          v-on:cancel-form="selectedBreak = null"
          v-on:change-values="onChangeFieldValues"
          v-on:reload-allowed-dates="onReloadEmployeeBreakAllowedDates"
          v-on:reload-timeline="onReloadEmployeeBreakTimeline"
          v-on:store-break="onEditBreak"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-else-if="hasEmployeeProfile"
        label="Редактирование сотрудника"
        v-bind:need-confirm="needConfirmEmployeeForm"
        v-on:cancel="resetEmployeeProfile">
        <form-employee-creation
          method="dialog"
          v-bind:is-open-login-settings-section="false"
          v-bind:cabinet-option-list="employeeProfileOptions.cabinetList"
          v-bind:confidentiality-level-list="employeeProfileOptions.confidentialityLevels"
          v-bind:employee-position-option-list="employeeProfileOptions.employeePositionList"
          v-bind:employee-role-list="employeeProfileOptions.employeeRoleList"
          v-bind:employee-specialty-option-list="employeeProfileOptions.employeeSpecialtyList"
          v-bind:has-egisz="employeeProfileOptions.hasEgisz"
          v-bind:initial-permissions="selectedEmployeePermission"
          v-bind:input-errors="employeeProfileInputErrors"
          v-bind:is-open-optional-section="isOpenOptionalSection"
          v-bind:is-open-egis-section="isOpenEgisSection"
          v-bind:nsi-position-list="employeeProfileOptions.nsiPositionList"
          v-bind:time-step-option-list="employeeProfileOptions.timeStepList"
          v-bind="employeeProfileProps"
          v-on:change-values="onChangeFieldValues"
          v-on:collapse-optional-section="onCollapseOptionalSection"
          v-on:collapse-egis-section="onCollapseEgisSection"
          v-on:change-password="onChangePassword"
          v-on:submit-editing="onSubmitEmployeeEdit"
        >
          <template slot="footer">
            <menu class="schedule-appointment__buttons">
              <button
                class="schedule-appointment__button-cancel"
                type="button"
                v-on:click="resetEmployeeProfile">Отмена</button>
              <button
                class="schedule-appointment__button-save"
                type="submit">Сохранить</button>
            </menu>
          </template>
        </form-employee-creation>
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="isShowAppointmentForm"
        v-bind:label="appointmentProps.appointmentId ? dialogAppointmentTitle : ''"
        v-on:cancel="onCloseAppointmentForm"
        v-bind:need-confirm="needConfirmAppointmentForm"
        class="side-absolute">
        <template v-slot:header>
          <div
            v-if="!appointmentProps.appointmentId"
            class="appointment-side-dialog__menu appointment-side-bar__categories"
            ref="timeTableSlotForms"
          >
            <button
              class="appointment-side-dialog__menu__item appointment-side-bar__categories__item"
              type="button"
              v-bind:class="{ select: selectedForm === $options.AppointmentForm }"
              v-on:click="selectedForm = $options.AppointmentForm"
            >{{ dialogAppointmentTitle }}</button>
            <button
              class="appointment-side-dialog__menu__item appointment-side-bar__categories__item"
              type="button"
              v-bind:class="{ select: selectedForm === $options.BreakEditForm }"
              v-on:click="selectedForm = $options.BreakEditForm"
            >Добавить перерыв</button>
          </div>
        </template>
        <form-appointment-creation
          v-if="selectedForm === $options.AppointmentForm"
          has-copy
          method="dialog"
          v-bind:is-selected-appointments="wasSelectedAppointment"
          v-bind:advertising-channel-list="advertisingChannels"
          v-bind:rejection-reason-list="rejectionReasons"
          v-bind:can-create-patients="canCreatePatients"
          v-bind:can-edit-initial="canEditInitial"
          v-bind:doctor-assistant-list="doctorAssistants"
          v-bind:employee-list="appointmentWorkEmployees"
          v-bind:employee-timeline="appointmentEmployeeTimeline"
          v-bind:employee-work-days="appointmentEmployeeWorkDays"
          v-bind:favorite-services="servicesWithFavorites.favorites"
          v-bind:initial-date="appointmentDate"
          v-bind:initial-employee-id="appointmentEmployeeId"
          v-bind:initial-month="appointmentMonth"
          v-bind:suggested-patient-id="appointmentPatientId"
          v-bind:crm-deal-id="appointmentCrmDealId"
          v-bind:initial-time-ends="appointmentTimeEnds"
          v-bind:initial-time-starts="appointmentTimeStarts"
          v-bind:input-errors="appointmentInputErrors"
          v-bind:is-await-request="isAppointmentAwaitRequest"
          v-bind:is-outdated="appointmentIsOutdated"
          v-bind:notification="notification"
          v-bind:matched-patients="appointmentMatchedPatients"
          v-bind:patient-list="patients"
          v-bind:service-list="servicesWithFavorites.all"
          v-bind:presets-duration="$options.presetsDuration"
          v-bind="appointmentProps"
          v-bind:has-view-patient-medical-card="hasViewPatientMedicalCard"
          v-bind:has-view-patient-invoices="hasViewPatientInvoices"
          v-bind:has-view-patient-visit="hasViewPatientVisit"
          v-on:came="onCameAppointment"
          v-on:check-waiting-patient="onCheckNotification"
          v-on:cancel="onCancelAppointment"
          v-on:change-values="onChangeFieldValues"
          v-on:clear-matched-patients="onClearMatchedPatients"
          v-on:close="onCloseAppointmentForm"
          v-on:confirm="onConfirmAppointment"
          v-on:copy="onCopy"
          v-on:create-advertising-channel="onCreateAdvertisingChannel"
          v-on:delete-rejection-reason="onDeleteRejectionReason"
          v-on:input-date="onInputAppointmentDate"
          v-on:input-employee="onInputAppointmentEmployee"
          v-on:input-month="onInputAppointmentMonth"
          v-on:input-times="onInputAppointmentTimes"
          v-on:match-patients="onMatchedPatients"
          v-on:open-visit="onOpenVisit"
          v-on:open-waitings="openWaitingList"
          v-on:reload-timeline="onReloadEmployeeDayTimeline"
          v-on:submit-creating="onSubmitAppointmentNewPatient"
          v-on:submit-editing="onSubmitAppointmentExistsPatient"
        />
        <form-employee-break-edit
          v-if="selectedForm === $options.BreakEditForm"
          v-bind:allowed-dates="employeeBreakAllowedDates"
          v-bind:initial-employee-id="employeeBreakId"
          v-bind:employee-list="appointmentWorkEmployees"
          v-bind:initial-date="employeeBreakIsoDate"
          v-bind:initial-ends-time="appointmentTimeEnds"
          v-bind:initial-starts-time="appointmentTimeStarts"
          v-bind:input-errors="breakInputErrors"
          v-bind:timeline="employeeBreakTimeline"
          v-bind:presets-duration="$options.presetsDuration"
          v-on:cancel-break="onCancelBreak"
          v-on:cancel-form="onCloseAppointmentForm"
          v-on:change-values="onChangeFieldValues"
          v-on:reload-allowed-dates="onReloadEmployeeBreakAllowedDates"
          v-on:reload-timeline="onReloadEmployeeBreakTimeline"
          v-on:store-break="onEditBreak"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-else-if="waiting"
        v-bind:label="waitingListFormTitle"
        v-bind:need-confirm="needConfirmWaitingForm"
        v-on:cancel="closeWaitingForm">
        <form-add-to-waiting-list
          v-bind:has-create-patients="canCreatePatients"
          v-bind:has-view-patient-phone="hasViewPatientPhone"
          v-bind:input-errors="waitingListInputErrors"
          v-bind:is-await-waiting-request="isAwaitWaitingRequest"
          v-bind:is-loading-patient-list="isWaitingListLoadingPatientList"
          v-bind:patient-list="waitingListPatients"
          v-bind:service-list="servicesWithFavorites.all"
          v-bind:favorite-services="servicesWithFavorites.favorites"
          v-bind:waiting="waiting"
          v-on:cancel="closeWaitingForm"
          v-on:change-values="onChangeFieldValues"
          v-on:submit-exist="onSubmitWaitingExistPatient"
          v-on:submit-new="onSubmitWaitingNewPatient"
          v-on:update-patient-search-query="onSearchWaitingListPatients" />
      </confirm-side-dialog>
      <side-dialog
        label="Свободное время для записи"
        v-else-if="waitingOpenTimes.length"
        v-on:cancel="closeWaitingOpenTimes">
        <waiting-open-times v-bind:items="waitingOpenTimes" />
      </side-dialog>
      <confirm-side-dialog
        label="Лист ожидания"
        v-else-if="showWaitingList"
        v-on:cancel="closeWaitingList"
      >
        <waiting-list
          v-bind:actions="waitingListActions"
          v-bind:list="selectedWaitings"
          v-on:create-waiting="onCreateWaiting"
          v-on:delete-waiting="onDeleteWaiting"
          v-on:initiate-call="onInitiateCallByPatient"
          v-on:view-waiting="onViewWaiting"
          v-on:make-appointment="onMakeAppointmentFromWaitingList"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        label="Свободные врачи"
        v-bind:size="'small'"
        v-else-if="shownFreeDoctorControlsDialog"
        v-on:cancel="closeFreeDoctorControlsDialog"
      >
        <time-step-minutes-picker
          v-on:change-time-step="onChangeTimeStepMinutes"
        />
        <employee-free-dates-controls-list
          v-bind:employees="employeeFreeDates"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        class="work-time-controls-side-dialog"
        label="Учет рабочего времени"
        v-bind:size="'normal'"
        v-if="shownWorkTimeControlsDialog"
        v-on:cancel="closeWorkTimeControlsDialog"
      >
        <router-link
          class="work-time-controls-side-dialog__link"
          v-bind:to="{ name: 'ClinicPanelWorkTimeAccountJournal' }">
          Перейти в журнал учета рабочего времени
        </router-link>
        <employee-work-time-controls-list
          class="work-time-controls-side-dialog__body"
          v-bind:employees="employeesForWorkTimeControls"
          v-bind:message="messageForWorkTimeControls"
          v-on:employee-counter-start="startEmployeeWorkTimeCounter"
          v-on:employee-counter-stop="stopEmployeeWorkTimeCounter" />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="shownNearestUnconfirmedAppointmentList"
        class="nearest-unconfirmed-appointment-dialog"
        label="Обзвон пациентов"
        v-bind:size="'small'"
        v-on:cancel="closeNearestUnconfirmedAppointmentList"
      >
        <nearest-unconfirmed-appointment-list
          class="nearest-unconfirmed-appointment-dialog__body"
          v-bind:actions="nearestUnconfirmedAppointmentActions"
          v-bind:list="nearestUnconfirmedAppointments"
          v-on:close="closeNearestUnconfirmedAppointmentList"
          v-on:edit-appointment="openAppointmentDialog"
          v-on:confirm-appointment="onConfirmAppointment"
          v-on:initiate-call="onInitiateCallByPatient"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="shownAggregatorOrdersDialog"
        class="aggregator-order-dialog"
        v-bind:size="'small'"
        v-bind:label="aggregatorDialogLabel"
        v-on:cancel="closeAggregatorOrdersDialog"
      >
        <aggregator-order-list
          class="aggregator-order-dialog__body"
          v-bind:orders="aggregator.orders"
          v-bind:cancelable="aggregator.cancelable"
          v-on:aggregator-cancel-order="onCancelAppointmentOrderFromAggregator"
          v-on:aggregator-make-order="onMakeAppointmentFromAggregatorOrder"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="shownWidgetOrdersDialog"
        class="aggregator-order-dialog"
        size="small"
        v-bind:label="widgetDialogLabel"
        v-on:cancel="closeWidgetOrdersDialog"
      >
        <aggregator-order-list
          class="aggregator-order-dialog__body"
          v-bind:orders="appointmentBookingWidget.orders"
          v-bind:cancelable="appointmentBookingWidget.cancelable"
          v-on:aggregator-cancel-order="onCancelAppointmentOrderFromWidget"
          v-on:aggregator-make-order="onMakeAppointmentFromWidgetOrder"
        />
      </confirm-side-dialog>
    </template>
  </appointment-schedule>
</template>
<script>
import AggregatorOrderList from '@/components/AggregatorOrderList.vue';
import AppointmentSideBar from '@/components/AppointmentSideBar.vue';
import DialogAddEmployeeBreak from '@/components/DialogAddEmployeeBreak.vue';
import EmployeeWorkTimeControlsList from '@/components/EmployeeWorkTimeControlsList.vue';
import FormAppointmentCreation from '@/components/FormAppointmentCreation.vue';
import SideDialog from '@/components/SideDialog.vue';
import TimeLine from '@/components/TimeLine.vue';
import dateMixin from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import { formatDate } from '@/utils/timeFormat';
import appointmentMixin, { AppointmentSlot, calcDifferenceMinutesForStringTime } from '@/mixins/appointmentMixin';
import TimetableColumnHeader from '@/components/TimetableColumnHeader.vue';
import MenuEmployeeAction from '@/components/MenuEmployeeAction.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import NearestUnconfirmedAppointmentList from '@/components/NearestUnconfirmedAppointmentList.vue';
import TimetableCell from '@/components/TimetableCell.vue';
import FormAddToWaitingList from '@/components/FormAddToWaitingList.vue';
import { MoveAppointmentRequest } from '@/storage/gateways/AppointmentScheduleGateway';
import employeeProfileMixin from '@/mixins/employeeProfileMixin';
import FormEmployeeCreation from '@/components/FormEmployeeCreation.vue';
import employeeBreakMixin from '@/mixins/employeeBreakMixin';
import FormEmployeeBreakEdit from '@/components/FormEmployeeBreakEdit.vue';
import FormDailyTimeRange from '@/components/FormDailyTimeRange.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import ContextMenuAppointmentSlot from '@/components/ContextMenuAppointmentSlot.vue';
import WaitingList from '@/components/WaitingList.vue';
import WaitingOpenTimes from '@/components/WaitingOpenTimes.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import AppointmentSchedule from '@/views/ClinicPanel/AppointmentSchedule.vue';
import AppointmentScheduleHead from '@/components/AppointmentScheduleHead.vue';
import EmployeeFreeDatesControlsList from '@/components/EmployeeFreeDatesControlsList.vue';
import TimeStepMinutesPicker from '@/components/TimeStepMinutesPicker.vue';
import printMixin from '@/mixins/printMixin';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

const AppointmentForm = FormAppointmentCreation.name;
const BreakEditForm = FormEmployeeBreakEdit.name;
const EmployeeForm = FormEmployeeCreation.name;
const WaitingForm = FormAddToWaitingList.name;

const needConfirm = (form) => ({ needConfirmBeforeClosed }) => needConfirmBeforeClosed[form];

/**
 * @description Компонент страницы графика приемов.
 * @type {Vue}
 */
export default {
  components: {
    TimeStepMinutesPicker,
    EmployeeFreeDatesControlsList,
    AggregatorOrderList,
    AppointmentSchedule,
    AppointmentScheduleHead,
    AppointmentSideBar,
    ConfirmSideDialog,
    ContextMenuAppointmentSlot,
    DialogAddEmployeeBreak,
    EmployeeWorkTimeControlsList,
    FormAddToWaitingList,
    FormAppointmentCreation,
    FormDailyTimeRange,
    FormEmployeeBreakEdit,
    FormEmployeeCreation,
    MenuEmployeeAction,
    ModalDialog,
    NearestUnconfirmedAppointmentList,
    PopoverMenu,
    SideDialog,
    TimeLine,
    TimetableCell,
    TimetableColumnHeader,
    WaitingList,
    WaitingOpenTimes,
  },
  computed: {
    loadedEmployeeListIsEmpty() {
      return !this.isAwaitReloadTimetable && !this.employee.days.length;
    },
    emptyMessage: ({ viewDate, isAwaitReloadTimetable }) => {
      if (!isAwaitReloadTimetable) {
        return `${viewDate} рабочих часов нет`;
      }
      return '';
    },
    /**
     * @description Запрашиваемый период.
     * @param {Date} day.
     * @param period
     * @param $options
     * @returns {Date | {form:Date, to:Date}}
     */
    range({ day, period, $options }) {
      if (period === $options.periodDay) {
        return day;
      }
      const from = new Date(day.toISOString());
      const to = new Date(day.toISOString());
      const fromDay = day.getDate() - day.getDay() + 1;
      const toDay = fromDay + 6;
      from.setDate(fromDay);
      to.setDate(toDay);
      return { from, to };
    },
    /**
     * @description Идентификатор выбранного сотрудника.
     * @returns {number}
     */
    employeeId: ({ $route }) => parseInt($route.params.id, 10),
    /**
     * @description Может взаимодействовать с перерывами сотрудника.
     * @returns {boolean}
     */
    hasEditBreaks() {
      return this.employee.actions.includes('breaks');
    },
    /**
     * @description Деактивировать кнопку экспорта в excel.
     * @returns {boolean}
     */
    isDisableExport() {
      if (!this.actions.includes('export-excel')) {
        return true;
      }
      if (this.isAwaitReloadTimetable) {
        return true;
      }
      return !this.employee.days.some((item) => item.slots.length > 0);
    },
    /**
     * @description Выбранная дата в ISO формате.
     * @returns {string}
     */
    isoDate: ({ day, formatDateToISO }) => formatDateToISO(day),
    /**
     * @description Данные выбранного сотрудника.
     * @returns {any}
     */
    selectedEmployee() {
      return (
        this.employee || {
          days: [],
          id: 0,
          name: '',
          positions: '',
        }
      );
    },
    /**
     * @description Запрос данных расписания.
     * @returns {any}
     */
    viewQuery() {
      return {
        category: this.category,
        date: this.isoDate,
        period: this.period,
        step: this.step,
        value: this.employeeId,
      };
    },
    viewDate: ({ isoDate }) => formatDate(new Date(isoDate)),
    viewDateTitle({ range, period }) {
      if (period === this.$options.periodDay) {
        return `Расписание на ${formatDate(range)}`;
      }
      const options = {
        month: 'long',
      };
      const year = range.to.getFullYear();
      return `Расписание на ${range.to.toLocaleDateString('ru-RU', options)} ${year}`;
    },
    needConfirmAppointmentForm: needConfirm(AppointmentForm),
    needConfirmBreakEditForm: needConfirm(BreakEditForm),
    needConfirmEmployeeForm: needConfirm(EmployeeForm),
    needConfirmWaitingForm: needConfirm(WaitingForm),
  },
  AppointmentForm,
  BreakEditForm,
  EmployeeForm,
  WaitingForm,
  created() {
    if (this.initialAppointmentId) {
      this.openAppointmentDialog(
        this.initialAppointmentId,
        this.formatDateToISO(this.day),
        { id: this.employeeId, name: '' },
      );
    }
    this.reloadTimetable(this.viewQuery);
    this.fetchWaitingList();
  },
  data() {
    const needConfirmBeforeClosed = {};
    needConfirmBeforeClosed[AppointmentForm] = false;
    needConfirmBeforeClosed[BreakEditForm] = false;
    needConfirmBeforeClosed[EmployeeForm] = false;
    needConfirmBeforeClosed[WaitingForm] = false;
    return {
      /**
       * @description Список кабинетов.
       * @type {({color: string, id: string, name: string}[])}
       */
      cabinets: [],
      selectedChangesWorkTimeDay: false,
      popoverChangesWorkTimeX: 0, // координаты для поповера
      popoverChangesWorkTimeY: 0, // координаты для поповера
      employee: {
        actions: [], id: 0, name: '', days: [],
      },
      breakTimeline: [],
      dateTitle: '',
      dayList: [],
      needConfirmBeforeClosed,
      selectedForm: AppointmentForm,
    };
  },
  methods: {
    /**
     * @description Обработать открытие диалогового окна.
     * @param {number} eventId
     * @param {string} isoDate
     * @param {{id:number,name:string}} employee
     * @param {string} endsTime
     * @param {string} startsTime
     * @returns {void}
     */
    onOpenDialog(eventId, isoDate, employee, endsTime, startsTime) {
      this.selectedForm = this.$options.AppointmentForm;
      this.openAppointmentDialog(eventId, isoDate, employee, endsTime, startsTime);
      this.openBreakForm(employee.id, isoDate);
    },
    /**
     * Очистить состояние открытого попапа
     */
    clearSelectedChangesWorkTimeDay() {
      this.selectedChangesWorkTimeDay = false;
    },
    /**
     * @description Обработать отправку базового времени работы.
     * @param {string} startTime Время начала.
     * @param {string} endTime   Время окончания.
     * @param {function} closer  Функция закрытия попапа.
     * @returns {void}
     */
    changeEmployeeWorkHours(startTime, endTime, closer) {
      closer();
      this.clearSelectedChangesWorkTimeDay();
      this.$workScheduleGateway
        .changeEmployeeWorkTimeHours(this.employeeId, endTime, startTime, this.isoDate)
        .then((response) => {
          this.onGatewayResponse(response);
        });
    },
    /**
     * @description Обработать ответ шлюза.
     * @param {Either} response Ответ.
     * @returns {void}
     */
    onGatewayResponse(response) {
      const onLeft = ({ title, tags }) => {
        if (tags && tags.includes('hight-importance')) {
          this.notifyImportanceFail(title);
        } else {
          this.notifyFail(title);
        }
      };
      const onRight = ({ message }) => {
        if (message) {
          this.notifySuccess(message);
          this.reloadTimetable(this.viewQuery);
        }
        if (this.closePopover) {
          this.closePopover();
        }
      };
      response.fold(onLeft, onRight);
    },
    /**
     * @description Проверить возможность перемещения записи на прием.
     * @param {any} day День.
     * @returns {function}
     */
    canMoveAppointment(day) {
      return (slotStart, slotSection = 0) => {
        const index = day.slots.findIndex((slot) => slot === slotStart);
        let size = index + this.dragAppointment.ratioToStep;
        for (let i = index; i < size; i += 1) {
          if (i >= day.slots.length) {
            return false;
          }
          const slot = day.slots[i];
          const isSelf = slot.id === this.dragAppointment.id;
          if (isSelf) {
            size = i + slotSection + 1;
          } else if (slot.tags.indexOf('add') === -1) {
            return false;
          }
        }
        return true;
      };
    },
    /**
     * @description Обработать перемещение записи на прием.
     * @param {string} time       Новое время начала записи на прием.
     * @param {string} employeeId Идентификатор сотрудника.
     * @returns {void}
     */
    onMoveAppointment(time, employeeId, day) {
      const startsAt = `${day.date} ${time}`;
      const request = new MoveAppointmentRequest(this.dragAppointment.id, employeeId, startsAt);
      this.moveAppointment(request);
    },
    /**
     * @description Обработать показ контекстного меню.
     * @param {number} isoDate Дата в формате ISO.
     * @param {{
     *  offset:any,
     *  currentStatus:string,
     *  slot:any,
     *  target:Element
     * }}              event Событие.
     */
    onOpenContextMenu(isoDate, {
      offset,
      currentStatus,
      slot,
      target,
    }) {
      const appointmentSlot = new AppointmentSlot(
        slot.actions,
        slot.tags,
        isoDate,
        slot.starts_time,
        slot.ends_time,
        slot.id || null,
        this.employeeId,
        null,
        slot.patient ? slot.patient.id : null,
        slot.has_next_appointments,
      );
      if (appointmentSlot.isOpenSlot() || appointmentSlot.isPatientSlot()) {
        this.openAppointmentSlotContextMenu(offset, currentStatus, appointmentSlot, target);
      }
    },
    /**
     * @description Обработать вставку скопированной записи на прием.
     * @returns {void}
     */
    onPasteAppointmentCopy() {
      this.appointmentSlotTarget.forEach((slot) => {
        const startsTime = slot.getStartsTime();
        const isoDate = slot.getIsoDate();
        const targetDay = this.employee.days.find((day) => day.date === isoDate);
        const slots = targetDay
          .slots
          .filter((daySlot) => daySlot.starts_time >= startsTime);
        const targetDuration = this.appointmentCopy.intervalDurationInMinutes;
        let calculatedSlot = slot;
        if (Number.isInteger(targetDuration) && targetDuration > 0) {
          const endsTimeDuration = this.prepareEndsTimeDurationForPasteAppointment(
            slots,
            startsTime,
            targetDuration,
          );
          calculatedSlot = slot
            .copyWithProps({
              endsTime: this.getEndsTimeByStartAndDuration(startsTime, endsTimeDuration),
            });
          if (endsTimeDuration !== targetDuration) {
            this.needConfirmAppointmentPaste = true;
            this.appointmentSlotForPaste = calculatedSlot;
            return;
          }
        }
        this.onPasteAppointment(calculatedSlot);
      });
    },
    /**
     * @description Обработать отмену перерыва сотрудника.
     * @param {number} breakId Идентификатор перерыва сотрудника.
     * @returns {void}
     */
    async onCancelBreak(breakId) {
      const result = await this.sendCancelBreakRequest(breakId);
      result.forEach(() => this.reloadTimetable(this.viewQuery));
    },
    /**
     * @description Скопировать запись на прием.
     * @returns {void}
     */
    onCopyAppointment() {
      this.appointmentSlotTarget.forEach((slot) => {
        const timeIntervalInMinutes = calcDifferenceMinutesForStringTime(
          slot.getStartsTime(),
          slot.getEndsTime(),
        );
        this.copyAppointmentById(
          slot.getAppointmentId(),
          slot.getEmployeeId(),
          timeIntervalInMinutes,
        );
      });
    },
    /**
     * @description Обработать редактирование перерыва сотрудника.
     * @param {EditEmployeeBreakRequest} request Запрос.
     * @returns {void}
     */
    async onEditBreak(request) {
      if (request.getBreakId()) {
        const result = await this.sendEditBreakRequest(request);
        result.forEach(() => this.reloadTimetable(this.viewQuery));
      } else {
        this.onAddEmployeeBreak(
          request.getEmployeeId(),
          request.toQuery().date,
          {
            time_range_list: [
              {
                from: request.toQuery().starts_time,
                to: request.toQuery().ends_time,
                date: new Date(request.toQuery().date),
              },
            ],
            note: request.toQuery().note,
          },
        );
        this.addEmployeeBreakReachGoal();
      }
    },
    /**
     * @description Создать объект слушателей событий действий сотрудника.
     * @param {number} employeeId Идентификатор сотрудника.
     * @returns {any}
     */
    createListenersEmployeeActions(employeeId) {
      const addBreak = (isoDate) => this.tryOpenEmployeeAddBreakDialog(employeeId, isoDate);
      const openEmployeeProfile = () => {
        this.fetchEmployeeProfileOptions();
        this.fetchEmployeeProfile(employeeId);
      };
      const setDayOff = (date) => {
        this.$workScheduleGateway
          .setEmployeeDayOff(employeeId, date)
          .then(this.onGatewayResponse);
      };
      const openEmployeeWorkTimeEdit = (employeId, x, y, day) => {
        this.selectedChangesWorkTimeDay = day;
        this.popoverChangesWorkTimeX = x;
        this.popoverChangesWorkTimeY = y;
      };
      return {
        'add-break': addBreak,
        'open-profile': openEmployeeProfile,
        'set-day-off': setDayOff,
        'change-work-time': openEmployeeWorkTimeEdit,
      };
    },
    /**
     * @description Перезагрузить данные таблицы.
     * @param {string} date Дата.
     * @param {number} step Шаг.
     * @returns {void}
     */
    async reloadTimetable({ date, step }) {
      this.fetchNearestUnconfirmedAppointments();
      this.fetchAppointmentOrders();
      this.fetchExpandedAppointments(date, this.employeeId, this.period);
      this.isAwaitReloadTimetable = true;
      try {
        this.onCloseAppointmentForm();
        const result = await this.$appointmentScheduleGateway.getViewByRange(
          date,
          this.employeeId,
          this.period,
          step,
        );
        result.forEach((data) => {
          this.actions = data.actions || [];
          this.appointmentNearList = data.nearAppointments;
          this.cabinets = data.cabinets;
          this.dateTitle = data.title;
          this.employee = data.employee;
          this.step = data.stepMinutes;
          this.timeline = data.timeline;
        });
      } finally {
        this.isAwaitReloadTimetable = false;
      }
    },
    /**
     * @description Получить расширенные записи на прием.
     * @param {string} date       Дата в формате ISO.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} period     Период.
     * @returns {void}
     */
    fetchExpandedAppointments(date, employeeId, period) {
      const onResponse = (response) => {
        response.forEach((result) => { this.expandedSlots = result; });
      };
      this
        .$appointmentScheduleGateway
        .getExpandedViewByRange(date, employeeId, period)
        .then(onResponse);
    },
    /**
     * @description Обработать редактирование профиля сотрудника.
     * @param {EmployeeEditRequest} request Запрос.
     * @returns {void}
     */
    onSubmitEmployeeEdit(request) {
      this
        .sendEmployeeProfileEditRequest(request)
        .then(() => {
          this.resetEmployeeProfile();
          this.reloadTimetable(this.viewQuery);
          this.fetchDoctorList();
        })
        .catch(() => {});
    },
    /**
     * @description Печать расписания.
     * @param {string} date Дата.
     * @param {Object} event Событие.
     * @returns {void}
     */
    printPage(date, event) {
      const hidePatientPhoneNumber = event.hidePhone ?? false;
      const printFreeSlots = event.printFreeSlots ?? false;
      const onSuccess = (response) => {
        const blob = new Blob([response], { type: 'text/html' });
        const container = this.$refs.printView;
        container.setAttribute('src', URL.createObjectURL(blob));
        container.onload = () => {
          container.contentWindow.print();
        };
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$appointmentScheduleGateway
        .viewAppointmentScheduleEmployeePrint(
          this.employeeId,
          date,
          hidePatientPhoneNumber,
          printFreeSlots,
          this.step,
        )
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Экспортировать расписание приемов в эксель.
     * @returns {void}
     */
    async exportToExcel() {
      const request = await this.$appointmentScheduleGateway.getSheetViewByRange(
        this.viewQuery.date,
        this.employeeId,
        this.period,
        this.viewQuery.step,
      );
      request.forEach((file) => {
        saveFile(file, 'Расписание.xlsx');
      });
    },
  },
  mixins: [
    appointmentMixin,
    dateMixin,
    employeeBreakMixin,
    employeeProfileMixin,
    notifierMixin,
    printMixin,
  ],
  name: 'ClinicPanelAppointmentScheduleEmployeePeriod',
};
</script>
